/* eslint-disable react/jsx-props-no-spreading */
import { useCallback, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { toast } from "react-toastify";
import { useQueryClient } from "@tanstack/react-query";
import getBrandsList from "api/brands/getBrandsList";
import getAgenciesList from "api/getAgenciesList";
import getPhasesFilterList from "api/getPhasesFilterList";
import getSellersList from "api/sellers/getSellersList";
import getProspectTitleList from "api/getProspectTitleList";
import Select from "components/common/Select";
import { URL_KEYS } from "constants/dashboard";
import parseErrorMessage from "helpers/parseErrorMessage";
import useEvent from "hooks/useEvent";
import reduceUnique from "helpers/reduceUnique";
import useRouterSearchParams from "hooks/useRouterSearchParams";
import { brandsListParamsKey } from "queryKeys/brandsListKey";
import { sellersListParamsKey } from "queryKeys/sellersListKey";
import { agenciesListParamsKey } from "queryKeys/getAgenciesListKey";
import { phasesListParamsKey } from "queryKeys/phasesListKey";
import { prospectTitleListParamsKey } from "queryKeys/prospectTitleList";
import STYLES from "./index.css";
import transformOptions from "./transformOptions";
import { ADDITIONAL, DEBOUNCE_TIMEOUT, PER_PAGE } from "./constants";

const SENTIMENT_OPTIONS = [
  { value: "Fire", label: "Fire" },
  { value: "Hot", label: "Hot" },
  { value: "Warm", label: "Warm" },
  { value: "Lukewarm", label: "Lukewarm" },
  { value: "Cold", label: "Cold" },
  { value: "Freezing", label: "Freezing" },
];

const contextByFilterName = {
  brand: {
    label: "Brand",
    queryKey: brandsListParamsKey,
    queryFn: getBrandsList,
    urlKey: "brandIds",
  },
  excludeBrand: {
    label: "Exclude Brands",
    queryKey: brandsListParamsKey,
    queryFn: getBrandsList,
    urlKey: "excludeBrandIds",
  },
  sentiment: {
    label: "Sentiment",
    isStatic: true,
    urlKey: "sentiments",
  },
  seller: {
    label: "Seller",
    queryKey: sellersListParamsKey,
    queryFn: getSellersList,
    urlKey: "sellerIds",
  },
  phase: {
    label: "Phase",
    queryKey: phasesListParamsKey,
    queryFn: getPhasesFilterList,
    urlKey: "phaseIds",
  },
  agency: {
    label: "Agency",
    queryKey: agenciesListParamsKey,
    queryFn: getAgenciesList,
    urlKey: "agencies",
  },
  title: {
    label: "Title",
    queryKey: prospectTitleListParamsKey,
    queryFn: getProspectTitleList,
    urlKey: "titles",
  },
};
const DELIMITER = "|||";

export default function MultiSelectCommonFilter({ filterName, ...props }) {
  const queryClient = useQueryClient();

  const { label, queryKey, queryFn, urlKey, isStatic } = contextByFilterName[filterName];

  const [searchParams, setSearchParams] = useRouterSearchParams();
  let dataValues;
  let dataNames;
  if (urlKey === "titles") {
    dataValues = searchParams.get(urlKey)?.split(DELIMITER) ?? null;
    dataNames = searchParams.get(`${filterName}Names`)?.split(DELIMITER) ?? null;
  } else {
    dataValues = searchParams.get(urlKey)?.split(",") ?? null;
    dataNames = searchParams.get(`${filterName}Names`)?.split(",") ?? null;
  }

  const data = useMemo(
    () =>
      dataValues?.length && dataNames?.length
        ? dataValues?.map((value, index) => ({
            label: dataNames[index],
            value,
          }))
        : [],
    [dataNames, dataValues],
  );

  const initOptions = data.length
    ? dataValues?.map((value, index) => ({
        label: dataNames[index],
        value,
      }))
    : [];

  const [{ isLoading, options }, setOptions] = useState({
    isLoading: false,
    options: initOptions,
  });

  const selected = useMemo(() => {
    const currentOptions = isStatic ? SENTIMENT_OPTIONS : options;

    return currentOptions.filter((option) =>
      data?.find(
        (dataValueOption) => dataValueOption.value?.toString() === option.value?.toString(),
      ),
    );
  }, [data, options, isStatic]);

  const params = new URLSearchParams(searchParams);

  const handleChange = useEvent((selectedOption, selectedOptions) => {
    if (selectedOptions?.action === "remove-value") {
      const value = selectedOptions.removedValue?.value;
      const optionLabel = selectedOptions.removedValue?.label;

      const newValues = dataValues.filter(
        (dataValue) => dataValue?.toString() !== value?.toString(),
      );
      const newNames = dataNames.filter((dataName) => dataName !== optionLabel);

      if (newValues?.length && newNames?.length) {
        if (urlKey === "titles") {
          params.set(urlKey, newValues.join(DELIMITER));
          params.set(`${filterName}Names`, newNames.join(DELIMITER));
        } else {
          params.set(urlKey, newValues.join(","));
          params.set(`${filterName}Names`, newNames.join(","));
        }
      } else {
        params.delete(urlKey);
        params.delete(`${filterName}Names`);
      }
    } else {
      const currentValues = dataValues ? [...dataValues] : [];
      const currentNames = dataNames ? [...dataNames] : [];

      selectedOption.forEach((option) => {
        const valStr = option?.value?.toString();
        if (!currentValues.includes(valStr)) {
          currentValues.push(valStr);
          currentNames.push(option?.label);
        }
      });

      const joinChar = urlKey === "titles" ? DELIMITER : ",";

      if (currentValues.length > 0) {
        params.set(urlKey, currentValues.join(joinChar));
        params.set(`${filterName}Names`, currentNames.join(joinChar));
      }

      if (filterName === "phase") {
        params.delete("excludePhaseName");
        params.delete("excludePhase");
      }

      if (params.has(URL_KEYS.page)) {
        params.delete(URL_KEYS.page);
      }
    }

    setSearchParams(params);
  });

  const loadOptions = useCallback(
    (search, loadedOptions, { page }) => {
      const offset = page > 0 ? page * PER_PAGE : 0;
      const queryParams = {
        page,
        perPage: PER_PAGE,
        search: search || undefined,
      };

      if (filterName === "phase" || filterName === "excludePhase") {
        const partnerId = searchParams.get("partnerId") || undefined;
        const excludePartner = searchParams.get("excludePartner") || undefined;

        queryParams.partnerId = partnerId ? Number(partnerId) : undefined;
        queryParams.excludePartner = excludePartner ? Number(excludePartner) : undefined;
      }

      setOptions((prevState) => ({ ...prevState, isLoading: true }));

      return queryClient
        .fetchQuery({
          queryKey: queryKey({ queryParams }),
          queryFn,
        })
        .then(({ data: newData }) => {
          const newOptions = newData?.data;
          const total = newData?.total;
          const transformedOptions = transformOptions(newOptions);
          setOptions((prevState) => ({
            isLoading: false,
            options: reduceUnique([...prevState.options, ...transformedOptions]),
          }));

          return {
            options: transformedOptions,
            hasMore: total > offset,
            additional: {
              page: page + 1,
            },
          };
        })
        .catch((error) => {
          toast.error(parseErrorMessage(error));

          setOptions((prevState) => ({ ...prevState, isLoading: false }));

          return {
            options: loadedOptions,
            hasMore: false,
            additional: {
              page,
            },
          };
        });
    },
    [filterName, queryClient, queryFn, queryKey, searchParams],
  );

  return (
    <Select
      label={label}
      variant="outlined"
      size="lg"
      styles={STYLES}
      placeholder={isLoading ? "Loading..." : "Select"}
      value={selected || []}
      onChange={handleChange}
      options={isStatic ? SENTIMENT_OPTIONS : undefined}
      loadOptions={isStatic ? undefined : loadOptions}
      debounceTimeout={DEBOUNCE_TIMEOUT}
      additional={ADDITIONAL}
      selectComponent={isStatic ? "select" : "async"}
      hideSelectedOptions={isStatic}
      isClearable={false}
      isGrey
      isSearchable
      isMulti
      {...props}
    />
  );
}

MultiSelectCommonFilter.propTypes = {
  filterName: PropTypes.string.isRequired,
};
